import { PaperClipIcon, CheckIcon } from '@heroicons/react/solid'
import { JobPostingJsonLd } from 'next-seo'
import React, { useEffect, useState } from 'react'
import SvgLogo from '#/assets/images/logos/tophap-logo-black.svg'
import Button from '#/components/Button'
import Link from '#/components/Link'
import Meta from '#/components/Meta/meta'
import OverlaySpinner from '#/components/OverlaySpinner'
import { host } from '#/configs'
import { logEvent } from '#/services/analytics'
import { SendResume } from '#/store/actions/user'
import { useInputState, useIsMounted } from '#/utils/hook'

type JobDetails = {
  job: Blog.Post
  asyncStatus: TopHap.GlobalState['status']
  sendResume: typeof SendResume
}

const Index: React.FC<JobDetails> = ({ job, sendResume, asyncStatus }) => {
  const [disabled, setDisabled] = useInputState(null)
  const [firstName, setFirstName] = useInputState('')
  const [lastName, setLastName] = useInputState('')
  const [file, setFile] = useInputState('')
  const [fileData, setFileData] = useInputState('')
  const [email, setEmail] = useInputState('')
  const [phoneNumber, setPhoneNumber] = useInputState('')
  const [websiteUrl, setWebsiteUrl] = useInputState('')
  const [github, setGithubUrl] = useInputState('')
  const [linkedin, setLinkedinUrl] = useInputState('')
  const [applicationSent, setApplicationSent] = useState(false)

  const title = job?.title

  function logSubmitSuccess(name: string) {
    logEvent('generate_lead', {
      content_type: 'careers',
      value: `${name}`
    })
  }

  useEffect(() => {
    if (asyncStatus[SendResume.type] === 'success' && isMounted.current) {
      logSubmitSuccess(title)
    }
  }, [asyncStatus[SendResume.type]])

  const isMounted = useIsMounted()

  async function onSubmit(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault()

    if (fileData === '') return setDisabled(true)

    sendResume({
      subject: title,
      firstName,
      lastName,
      email,
      phoneNumber,
      linkedin,
      github,
      resume: fileData
    })

    setApplicationSent(true)
  }

  const onChange = (e: {
    target: { name: any; files: FileList | null; value: any }
  }) => {
    switch (e.target.name) {
      case 'selectedFile':
        if (e.target.files && e.target.files.length > 0) {
          setFile({ fileName: e.target.files[0].name })
        }
        break
      default:
        setFile({ [e.target.name]: e.target.value })
    }

    const reader = new FileReader()
    e.target.files && reader.readAsBinaryString(e.target.files[0])

    reader.onload = function () {
      setFileData(btoa(reader.result as string))
    }
  }

  const fileUpload = file.fileName ? (
    <span>{file.fileName}</span>
  ) : (
    <span>Upload Resume</span>
  )

  function formatPhoneNumber(value: string) {
    if (!value) return value

    const phoneNumber = value.replace(/[^\d]/g, '')

    const phoneNumberLength = phoneNumber.length

    if (phoneNumberLength < 4) return phoneNumber

    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`
    }

    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
      3,
      6
    )}-${phoneNumber.slice(6, 10)}`
  }

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhoneNumber = formatPhoneNumber(e.target.value)
    setPhoneNumber(formattedPhoneNumber)
  }

  const inputStyles =
    'block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:border-gray-300 focus:ring-0 sm:text-sm'

  const meta = {
    title: job?.title,
    openGraph: {
      opengraphTitle:
        "TopHap is a small team of developers and designers building tools to help real estate professionals research more effectively. Come join our team that's reinventing real estate research.",
      opengraphDescription:
        "TopHap is a small team of developers and designers building tools to help real estate professionals research more effectively. Come join our team that's reinventing real estate research."
    },
    image: `${host}/images/blog/tophap-blog.jpg`,
    site_name: 'TopHap | Careers'
  }

  return (
    <div className='bg-gray-50 '>
      <Meta meta={meta} />
      <JobPostingJsonLd
        applicantLocationRequirements='FR'
        baseSalary={{
          currency: 'US',
          value: [0, 120],
          unitText: 'MONTH'
        }}
        datePosted={job?.date}
        description={job?.excerpt + ''}
        employmentType={['FULL_TIME', 'CONTRACTOR', 'PART_TIME']}
        hiringOrganization={{
          name: 'TopHap',
          sameAs: 'www.tophap.com'
        }}
        jobLocation={{
          streetAddress: '360 Hartz Ave',
          addressLocality: 'Danville',
          addressRegion: 'California',
          postalCode: '94526',
          addressCountry: 'United States'
        }}
        title={job?.title}
        validThrough='2022-01-06'
      />

      <div className='bg-white pt-10 sm:pt-8'>
        <div className='mx-auto max-w-2xl px-6 lg:px-0'>
          <SvgLogo className='h-9 w-auto text-black' />

          <h1 className='pt-10 text-3xl'>{title}</h1>
          <p className='prose mr-auto pb-8 pt-6 text-sm text-gray-500 sm:text-base'>
            Thank you for considering a career at TopHap. Please take a minute
            to fill out the following form. After you have completed your
            application an email will be sent to you to confirm your application
            was successful.
          </p>
        </div>
      </div>
      {applicationSent && asyncStatus[SendResume.type] !== 'request' ? (
        <div className='mx-auto flex justify-center'>
          <div className='inline-block overflow-hidden rounded-lg px-4 pb-4 pt-5 text-left align-bottom transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6 sm:align-middle'>
            <div>
              <div className='mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100'>
                <CheckIcon
                  aria-hidden='true'
                  className='h-6 w-6 text-green-600'
                />
              </div>
              <div className='mt-3 text-center sm:mt-5'>
                <div className='text-lg font-medium leading-6 text-gray-900'>
                  Application submitted!
                </div>
              </div>
            </div>
            <div className='mt-5 text-center sm:mt-6'>
              <Link href='/careers'>
                <button
                  className='inline-flex w-full max-w-sm items-center justify-center rounded-md border border-transparent bg-gradient-to-r from-primary to-gradient-blue px-4 py-3 text-base font-medium leading-6 text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
                  type='button'
                >
                  Return to the main page
                </button>
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className='mx-auto my-8 max-w-2xl overflow-hidden rounded-md bg-white p-6 sm:shadow '>
          <div className='relative mx-auto'>
            <div className='text-left'>
              <h2 className='text-xl font-semibold tracking-tight text-gray-900 sm:text-xl'>
                Submit Your Application
              </h2>
            </div>
            <div className='mt-8'>
              <form onSubmit={onSubmit}>
                <div className='grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8'>
                  <div className='col-span-2 grid'>
                    <label
                      className='block text-sm font-medium text-gray-700'
                      htmlFor='resume'
                    >
                      Resume/CV (.pdf only)
                    </label>
                    <div className='mt-1'>
                      <label className='inline-flex w-auto max-w-xs cursor-pointer items-center truncate rounded-md border border-transparent bg-gray-300 px-3 py-2 text-sm font-medium leading-4 text-gray-500 text-opacity-80 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'>
                        <PaperClipIcon
                          aria-hidden='true'
                          className='-ml-0.5 mr-2 h-4 w-4'
                        />
                        <span className='w-auto truncate text-sm leading-normal'>
                          {fileUpload}
                        </span>

                        <input
                          accept='.pdf,.doc,.docx'
                          className='hidden'
                          id='resume'
                          name='selectedFile'
                          type='file'
                          onChange={e => onChange(e)}
                        />
                      </label>
                      {disabled && (
                        <div className='mt-1 text-xs text-red-500'>
                          Required field
                          <span className='text-red-500'>*</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='col-span-2 grid sm:col-span-1'>
                    <label
                      className='block text-sm font-medium text-gray-700'
                      htmlFor='first_name'
                    >
                      First name
                      <span className='text-red-500'>*</span>
                    </label>
                    <div className='mt-1'>
                      <input
                        autoComplete='given-name'
                        className={inputStyles}
                        id='first_name'
                        name='fname'
                        required
                        type='text'
                        value={firstName}
                        onChange={e => setFirstName(e)}
                      />
                    </div>
                  </div>
                  <div className='col-span-2 grid sm:col-span-1'>
                    <label
                      className='block text-sm font-medium text-gray-700'
                      htmlFor='last_name'
                    >
                      Last name
                      <span className='text-red-500'>*</span>
                    </label>
                    <div className='mt-1'>
                      <input
                        autoComplete='family-name'
                        className={inputStyles}
                        id='last_name'
                        name='lname'
                        required
                        type='text'
                        value={lastName}
                        onChange={e => setLastName(e)}
                      />
                    </div>
                  </div>
                  <div className='col-span-2 grid sm:col-span-1'>
                    <label
                      className='block text-sm font-medium text-gray-700'
                      htmlFor='email'
                    >
                      Email
                      <span className='text-red-500'>*</span>
                    </label>
                    <div className='mt-1'>
                      <input
                        autoComplete='email'
                        className={inputStyles}
                        id='email'
                        name='email'
                        required
                        type='email'
                        value={email}
                        onChange={e => setEmail(e)}
                      />
                    </div>
                  </div>
                  <div className='col-span-2 grid sm:col-span-1'>
                    <label
                      className='block text-sm font-medium text-gray-700'
                      htmlFor='phone_number'
                    >
                      Phone number
                      <span className='text-red-500'>*</span>
                    </label>

                    <div className='relative mt-1 rounded-md shadow-sm'>
                      <input
                        autoComplete='tel'
                        className={inputStyles}
                        id='phone_number'
                        name='phone'
                        placeholder='(555) 987-6543'
                        required
                        type='tel'
                        value={phoneNumber}
                        onChange={e => handleInput(e)}
                      />
                    </div>
                  </div>
                </div>

                <div className='grid gap-4 py-6 sm:grid-cols-3'>
                  <div className='col-span-2 grid sm:col-span-1'>
                    <label
                      className='block text-sm font-medium text-gray-700'
                      htmlFor='linkedin'
                    >
                      Linkedin
                    </label>
                    <div className='mt-1'>
                      <input
                        className={inputStyles}
                        id='linkedin'
                        type='text'
                        value={linkedin}
                        onChange={e => setLinkedinUrl(e)}
                      />
                    </div>
                  </div>
                  <div className='col-span-2 grid sm:col-span-1'>
                    <label
                      className='block text-sm font-medium text-gray-700'
                      htmlFor='github'
                    >
                      Github
                    </label>
                    <div className='mt-1'>
                      <input
                        className={inputStyles}
                        id='github'
                        type='text'
                        value={github}
                        onChange={e => setGithubUrl(e)}
                      />
                    </div>
                  </div>
                  <div className='col-span-2 grid sm:col-span-1'>
                    <label
                      className='block text-sm font-medium text-gray-700'
                      htmlFor='website'
                    >
                      Website
                    </label>
                    <div className='mt-1'>
                      <input
                        className={inputStyles}
                        id='website'
                        type='text'
                        value={websiteUrl}
                        onChange={e => setWebsiteUrl(e)}
                      />
                    </div>
                  </div>
                </div>

                <div className='sm:col-span-2'>
                  <Button
                    className='inline-flex w-full items-center justify-center rounded-md border border-transparent bg-gradient-to-r from-primary to-gradient-blue px-6 py-3 text-base  font-medium leading-6 text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
                    type='submit'
                  >
                    Submit application
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <OverlaySpinner
        absolute
        visible={asyncStatus[SendResume.type] === 'request'}
      />
    </div>
  )
}

export default Index
